import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Typography,
  Box,
  Chip,
  CircularProgress,
  useTheme,
  Collapse,
  IconButton,
  Autocomplete,
  TextField
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import shadowPropertiesConfig from '../../config/deviceShadowProperties.json';

/**
 * Styled components with Waltero branding
 */
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
  boxShadow: '0px 2px 12px rgba(2, 56, 97, 0.08)',
  overflow: 'auto',
  maxHeight: '600px',
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: 0,
  }
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.waltero.primary.backgroundBlue,
    color: theme.palette.waltero.primary.coreBlue,
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: `2px solid ${theme.palette.waltero.primary.neutralBlue}`,
    padding: theme.spacing(1, 1.5),
    position: 'sticky',
    top: 0,
    zIndex: 10,
    whiteSpace: 'nowrap',
    '&.property-cell': {
      position: 'sticky',
      left: 0,
      zIndex: 11,
      backgroundColor: theme.palette.waltero.primary.backgroundBlue,
      minWidth: '200px',
    }
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.waltero.secondary.backgroundApricot + '20',
  },
  '&:hover': {
    backgroundColor: theme.palette.waltero.primary.backgroundBlue,
  },
  '& .MuiTableCell-body': {
    fontSize: '0.75rem',
    padding: theme.spacing(0.75, 1.5),
    borderBottom: `1px solid ${theme.palette.waltero.primary.neutralBlue}40`,
    '&.property-cell': {
      position: 'sticky',
      left: 0,
      backgroundColor: 'inherit',
      fontWeight: 500,
      zIndex: 9,
      borderRight: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
    }
  }
}));

const ConfigCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'match',
})(({ theme, match }) => ({
  textAlign: 'center !important',
  backgroundColor: match === true
    ? theme.palette.success.light + '40'
    : match === false
    ? theme.palette.error.light + '40'
    : theme.palette.grey[100] + '40',
  minWidth: '100px',
  maxWidth: '150px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: match === true
      ? theme.palette.success.light + '60'
      : match === false
      ? theme.palette.error.light + '60'
      : theme.palette.grey[100] + '60',
  }
}));

const CategoryRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.waltero.primary.coreBlue + '15',
  '& .MuiTableCell-root': {
    fontWeight: 700,
    fontSize: '0.875rem',
    color: theme.palette.waltero.primary.coreBlue,
    textTransform: 'uppercase',
    borderBottom: `2px solid ${theme.palette.waltero.primary.coreBlue}`,
    padding: theme.spacing(1, 1.5),
  }
}));

/**
 * Helper function to get nested property value
 */
const getNestedValue = (obj, path) => {
  if (!obj || !path) return null;

  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return null;
    }
    current = current[key];
  }

  return current !== undefined ? current : null;
};

/**
 * Format value for display
 */
const formatValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * Check if value is short enough to display directly
 */
const isShortValue = (value) => {
  const formatted = formatValue(value);
  return formatted.length <= 5;
};

/**
 * Shadow Configuration Table Component
 * Displays device shadow properties with reported vs desired comparison
 */
const ShadowConfigurationTable = ({ configurations, devices, loading }) => {
  const theme = useTheme();
  const [selectedProperties, setSelectedProperties] = useState([]);

  // Initialize all categories as expanded
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const initialState = {};
    Object.keys(shadowPropertiesConfig).forEach(category => {
      initialState[category] = true;
    });
    return initialState;
  });

  // Build flat list of all properties for autocomplete
  const allPropertyOptions = useMemo(() => {
    const options = [];
    Object.entries(shadowPropertiesConfig).forEach(([category, properties]) => {
      properties.forEach(property => {
        options.push({
          label: `${category}.${property}`,
          category: category,
          property: property
        });
      });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  // Group all properties by category with filtering
  const categorizedProperties = useMemo(() => {
    const categorized = Object.entries(shadowPropertiesConfig).map(([category, properties]) => {
      let filteredProperties = properties || [];

      // If properties are selected, filter to only show those
      if (selectedProperties.length > 0) {
        const selectedInCategory = selectedProperties
          .filter(p => p.category === category)
          .map(p => p.property);

        filteredProperties = properties.filter(prop =>
          selectedInCategory.includes(prop)
        );
      }

      return {
        category,
        properties: filteredProperties
      };
    });

    // Filter out categories with no properties
    return categorized.filter(cat => cat.properties.length > 0);
  }, [selectedProperties]);

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Handle property selection change
  const handlePropertyChange = (event, newValue) => {
    setSelectedProperties(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (!devices || devices.length === 0) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">
          No devices found for this organization
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, color: theme.palette.waltero.primary.coreBlue }}>
        Device Shadow Configuration Comparison
      </Typography>

      {/* Property Filter Autocomplete */}
      <Box sx={{ mb: 3 }}>
        <Autocomplete
          multiple
          id="property-filter"
          options={allPropertyOptions}
          value={selectedProperties}
          onChange={handlePropertyChange}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Filter Properties"
              placeholder={selectedProperties.length === 0 ? "Showing all properties - select to filter" : "Add more properties..."}
              helperText={`${selectedProperties.length === 0 ? 'All properties shown' : `${selectedProperties.length} ${selectedProperties.length === 1 ? 'property' : 'properties'} selected`}`}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: theme.palette.waltero.primary.neutralBlue,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.waltero.primary.coreBlue,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.waltero.primary.coreBlue,
                  },
                }
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option.label}
                {...getTagProps({ index })}
                size="small"
                sx={{
                  backgroundColor: theme.palette.waltero.primary.coreBlue,
                  color: 'white',
                  '& .MuiChip-deleteIcon': {
                    color: 'white',
                    '&:hover': {
                      color: theme.palette.waltero.secondary.apricot,
                    }
                  }
                }}
              />
            ))
          }
          sx={{ backgroundColor: 'white' }}
        />
      </Box>

      <StyledTableContainer component={Paper}>
        <Table stickyHeader size="small">
          <StyledTableHead>
            <TableRow>
              <TableCell className="property-cell">Property</TableCell>
              {devices.map((device) => (
                <TableCell
                  key={device.id}
                  align="center"
                  sx={{
                    minWidth: '100px',
                    maxWidth: '150px',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                  }}
                >
                  <Tooltip title={`Device ID: ${device.id}`}>
                    <span>{device.serialnumber || device.name || 'Unknown'}</span>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {categorizedProperties.map(({ category, properties }) => (
              <React.Fragment key={category}>
                {/* Category Header Row */}
                <CategoryRow>
                  <TableCell colSpan={devices.length + 1}>
                    <Box display="flex" alignItems="center">
                      <IconButton
                        size="small"
                        onClick={() => toggleCategory(category)}
                        sx={{ mr: 1, color: theme.palette.waltero.primary.coreBlue }}
                      >
                        {expandedCategories[category] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                      {category.replace(/_/g, ' ')}
                      <Chip
                        label={properties.length}
                        size="small"
                        sx={{ ml: 2, backgroundColor: theme.palette.waltero.primary.coreBlue, color: 'white' }}
                      />
                    </Box>
                  </TableCell>
                </CategoryRow>

                {/* Property Rows */}
                <TableRow>
                  <TableCell colSpan={devices.length + 1} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expandedCategories[category] !== false} timeout="auto" unmountOnExit>
                      <Table size="small">
                        <TableBody>
                          {properties.map((property) => (
                            <StyledTableRow key={`${category}.${property}`}>
                              <TableCell className="property-cell">
                                <Tooltip title={`Path: ${category}.${property}`}>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    {property}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              {devices.map((device) => {
                                const deviceConfig = configurations?.[device.id];
                                const reported = getNestedValue(deviceConfig?.reported?.[category], property);
                                const desired = getNestedValue(deviceConfig?.desired?.[category], property);

                                // Determine match state: true (green), false (red), null (gray)
                                let matchState = null; // Default: gray (no data)

                                if (reported !== null && desired !== null) {
                                  // Both values exist - compare them
                                  matchState = JSON.stringify(reported) === JSON.stringify(desired);
                                } else if (reported !== null && desired === null) {
                                  // Reported exists but desired doesn't - mismatch
                                  matchState = false;
                                } else if (reported === null && desired !== null) {
                                  // Desired exists but reported doesn't - mismatch
                                  matchState = false;
                                }
                                // else both null - keep matchState as null (gray)

                                const displayValue = formatValue(reported);
                                const isShort = isShortValue(reported);

                                return (
                                  <Tooltip
                                    key={device.id}
                                    title={
                                      <Box>
                                        <Typography variant="caption" display="block">
                                          <strong>Reported:</strong> {formatValue(reported)}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          <strong>Desired:</strong> {formatValue(desired)}
                                        </Typography>
                                        {matchState === true && (
                                          <Typography variant="caption" display="block" color="success.light">
                                            ✓ Values match
                                          </Typography>
                                        )}
                                        {matchState === false && (
                                          <Typography variant="caption" display="block" color="error.light">
                                            ✗ Values differ
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                    arrow
                                  >
                                    <ConfigCell match={matchState}>
                                      {reported === null ? '-' : (isShort ? displayValue : '*')}
                                    </ConfigCell>
                                  </Tooltip>
                                );
                              })}
                            </StyledTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>

      <Box mt={2} display="flex" gap={2} alignItems="center">
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: theme.palette.success.light + '40',
              border: '1px solid' + theme.palette.success.main
            }}
          />
          <Typography variant="caption">Match</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: theme.palette.error.light + '40',
              border: '1px solid' + theme.palette.error.main
            }}
          />
          <Typography variant="caption">Mismatch</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: theme.palette.grey[100] + '40',
              border: '1px solid' + theme.palette.grey[400]
            }}
          />
          <Typography variant="caption">No Data</Typography>
        </Box>
        <Typography variant="caption" sx={{ ml: 2, fontStyle: 'italic' }}>
          * = Hover to see full value
        </Typography>
      </Box>
    </Box>
  );
};

export default ShadowConfigurationTable;
